/* eslint-disable @typescript-eslint/no-explicit-any */
import { FilterCheckIcon } from "@/components/icons/FilterCheckIcon";
import { FilterIcon } from "@/components/icons/FilterIcon";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import InfoTooltip from "@/components/ui/InfoTooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { convertLocalTimezone } from "@/utils/dateUtils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useDashboard } from "@/contexts/DashboardContext";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ExternalLink, X } from "lucide-react";
import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { fetchCallDetailOverviewPaged } from "@/lib/axios/api/call-detail-overview.api";
import { fetchCallDetailOverviewColumnFilter } from "@/lib/axios/api/call-detail-overview.api";
import type { CallDetailOverviewRow } from "@/types/call-detail-overview";
import type { CallOverviewColumnFilterItem } from "@/types/call-detail-overview";
import CallQualityStreamTimeline from "@/components/widgets/call-overview/CallQualityStreamTimeline";
import { CQD_COLORS_LIGHT, CQD_COLORS_DARK } from "./CallQualityDistributionWidget";

export type CallQualityStatus = "Good" | "Poor" | "Bad" | "Unclassified";

export interface CallQualityDetailRow {
  callId: string;
  /** Maps to GET /segment-stream/{callUniqueId} */
  callUniqueId: number | null;
  startTime: string;
  participants: string;
  duration: string;
  qualityScore: number | null;
  qualityStatus: CallQualityStatus;
}

const CQD_COLUMNS = [
  { title: "Start Time", key: "startTime", minWidth: 140, maxWidth: 180, filterable: true },
  { title: "Participants", key: "participantExtended", minWidth: 200, maxWidth: 320, filterable: true },
  { title: "Duration", key: "duration", minWidth: 90, maxWidth: 110, filterable: true },
  { title: "Quality", key: "QualityLevel", minWidth: 140, maxWidth: 180, filterable: true },
  { title: "Detail", key: "detail", minWidth: 100, maxWidth: 120, filterable: false },
];

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const getUniqueValues = (data: CallQualityDetailRow[], key: string): string[] => {
  const set = new Set<string>();
  data.forEach((row) => {
    if (key === "QualityLevel") set.add(row.qualityStatus);
    else if (key === "participants" || key === "participantExtended") set.add(String(row.participants));
    else if (key in row) {
      const v = (row as any)[key];
      if (v != null && v !== "") set.add(String(v));
    }
  });
  return Array.from(set);
};

/** Màu cột Quality = CQD_COLORS_LIGHT / CQD_COLORS_DARK (cùng chart Call Quality Distribution) */
const getQualityBadgeStyle = (
  status: CallQualityStatus,
  theme: "light" | "dark"
): { backgroundColor: string; color: string } => {
  const light = theme === "light";
  const colors = light ? CQD_COLORS_LIGHT : CQD_COLORS_DARK;
  const darkText = "#0C3451";
  const whiteText = "#fff";
  switch (status) {
    case "Good":
      return { backgroundColor: colors[0], color: whiteText };
    case "Poor":
    case "Bad":
      return { backgroundColor: colors[1], color: darkText };
    case "Unclassified":
    default:
      return { backgroundColor: colors[2], color: whiteText };
  }
};

const FilterDropdown: React.FC<{
  colKey: string;
  colTitle: string;
  uniqueValues: string[];
  selected: string[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  onValueToggle: (value: string) => void;
  onClearColumn: () => void;
  onSelectAllColumn: (values: string[]) => void;
}> = React.memo(
  ({
    colKey,
    colTitle,
    uniqueValues,
    selected,
    searchValue,
    onSearchChange,
    onValueToggle,
    onClearColumn,
    onSelectAllColumn,
  }) => {
    const debouncedSearch = useDebounce(searchValue, 200);
    const filteredValues = useMemo(() => {
      if (!debouncedSearch) return uniqueValues;
      const lower = debouncedSearch.toLowerCase();
      return uniqueValues.filter((v) => v.toLowerCase().includes(lower));
    }, [uniqueValues, debouncedSearch]);

    return (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 min-w-[24px] min-h-[24px] flex-shrink-0"
            aria-label="Filter column"
          >
            {selected.length > 0 ? (
              <FilterCheckIcon className="h-3 w-3" />
            ) : (
              <FilterIcon className="h-3 w-3" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-[220px] max-h-[80vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-2">
            <p className="text-xs font-medium mb-2">Filter by values:</p>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search values..."
                className="flex-1 text-sm bg-gray-50 dark:bg-[#3C5C73] border-gray-200 dark:border-white text-primary dark:text-white placeholder-gray-500"
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
              />
              {searchValue && (
                <Button variant="ghost" size="sm" onClick={() => onSearchChange("")}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="max-h-[150px] overflow-y-auto pr-2 space-y-1 mt-2">
              {filteredValues.length === 0 ? (
                <div className="text-xs text-gray-500 text-center py-2">No results found</div>
              ) : (
                filteredValues.map((value) => (
                  <div key={value} className="flex items-center gap-2 py-1">
                    <input
                      type="checkbox"
                      id={`${colKey}-${value}`}
                      checked={selected.includes(value)}
                      onChange={() => onValueToggle(value)}
                      className="h-4 w-4 rounded border-gray-300 flex-shrink-0"
                    />
                    <label
                      htmlFor={`${colKey}-${value}`}
                      className="text-xs truncate max-w-[160px] cursor-pointer leading-4 text-primary dark:text-white"
                    >
                      {colKey === "startTime" ? convertLocalTimezone(value) : value}
                    </label>
                  </div>
                ))
              )}
            </div>
            <div className="flex justify-between mt-4 pt-2 border-t border-gray-200 dark:border-[#4A6B85]">
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1 text-xs hover:bg-success hover:border-success hover:text-black dark:bg-[#3C5C73] dark:border-[#4A6B85] dark:text-white dark:hover:bg-success dark:hover:border-success dark:hover:text-black"
                onClick={onClearColumn}
              >
                Clear
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1 text-xs hover:bg-success hover:border-success hover:text-black dark:bg-[#3C5C73] dark:border-[#4A6B85] dark:text-white dark:hover:bg-success dark:hover:border-success dark:hover:text-black"
                onClick={() => onSelectAllColumn(uniqueValues)}
              >
                Select All
              </Button>
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
);

/** Map cột CQD sang tên cột API call-widget/overview */
const CQD_TO_API_COLUMN: Record<string, string> = {
  duration: "callDuration",
  QualityLevel: "qualityLevel",
};

function parseCallUniqueId(row: CallDetailOverviewRow): number | null {
  const raw = row.callUniqueId ?? (row as { CallUniqueId?: unknown }).CallUniqueId;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim() !== "") {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function mapOverviewRowToCQDRow(row: CallDetailOverviewRow): CallQualityDetailRow {
  const qualityLevel = String(row.qualityLevel ?? "");
  const qualityStatus: CallQualityStatus =
    qualityLevel === "Good" || qualityLevel === "Poor" || qualityLevel === "Bad"
      ? (qualityLevel as CallQualityStatus)
      : "Unclassified";
  const participantExtended = row.participantExtended ?? row.ParticipantExtended ?? row.participants;
  return {
    callId: row.callId,
    callUniqueId: parseCallUniqueId(row),
    startTime: row.startTime,
    participants: participantExtended ?? "",
    duration: row.callDuration ?? "",
    qualityScore: row.qualityScore ?? null,
    qualityStatus,
  };
}

export interface CallQualityDetailWidgetDateParams {
  startDate: string;
  endDate: string;
  previousStartDate?: string;
  previousEndDate?: string;
  queueMicrosoftIds?: string[];
  agentMicrosoftIds?: string[];
  groupIds?: number[];
  callMicrosoftIds?: string[];
}

interface CallQualityDetailWidgetProps {
  dateParams: CallQualityDetailWidgetDateParams;
  title?: string;
  limit?: number;
  onViewDetails?: (row: CallQualityDetailRow) => void;
  qualityLevelFilter?: "Good" | "Poor" | "Unclassified";
  onClearExternalFilters?: () => void;
}

/** Cột CQD dùng cho API filter (tên backend) */
const CQD_FILTER_COLUMN_NAMES = ["startTime", "participants", "callDuration", "qualityLevel"];

const CallQualityDetailWidget: React.FC<CallQualityDetailWidgetProps> = ({
  dateParams,
  title = "Call Quality Detail",
  limit = 20,
  onViewDetails,
  qualityLevelFilter,
  onClearExternalFilters,
}) => {
  const { theme } = useDashboard();
  const [sortKey, setSortKey] = useState<string>("startTime");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [searchValues, setSearchValues] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pagedData, setPagedData] = useState<{
    data: CallQualityDetailRow[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }>({ data: [], total: 0, page: 1, limit, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [columnFilterData, setColumnFilterData] = useState<CallOverviewColumnFilterItem[]>([]);
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);
  const overviewAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setFilters((prev) => {
      const currentQualityValues = prev.QualityLevel ?? [];
      if (qualityLevelFilter) {
        if (currentQualityValues.length === 1 && currentQualityValues[0] === qualityLevelFilter) {
          return prev;
        }
        return { ...prev, QualityLevel: [qualityLevelFilter] };
      }

      if (currentQualityValues.length === 0) return prev;
      const { QualityLevel: _removed, ...rest } = prev;
      return rest;
    });
    setCurrentPage(1);
  }, [qualityLevelFilter]);

  const apiSortColumn = CQD_TO_API_COLUMN[sortKey] ?? sortKey;
  const filterArray = useMemo(() => {
    const mappedFilters = Object.entries(filters)
      .filter(([, v]) => Array.isArray(v) && v.length > 0)
      .map(([column, value]) => ({
        column: CQD_TO_API_COLUMN[column] ?? column,
        value: value as string[],
      }));

    const hasQualityLevelFilter = mappedFilters.some((item) => item.column === "qualityLevel");
    if (!hasQualityLevelFilter && qualityLevelFilter) {
      mappedFilters.push({
        column: "qualityLevel",
        value: [qualityLevelFilter],
      });
    }

    return mappedFilters;
  }, [filters, qualityLevelFilter]);

  const fetchOverview = useCallback(() => {
    if (!dateParams?.startDate || !dateParams?.endDate) return;
    if (overviewAbortRef.current) overviewAbortRef.current.abort();
    const controller = new AbortController();
    overviewAbortRef.current = controller;
    setLoading(true);
    fetchCallDetailOverviewPaged(
      {
        startDate: dateParams.startDate,
        endDate: dateParams.endDate,
        previousStartDate: dateParams.previousStartDate,
        previousEndDate: dateParams.previousEndDate,
        queueMicrosoftIds: dateParams.queueMicrosoftIds ?? undefined,
        agentMicrosoftIds: dateParams.agentMicrosoftIds ?? undefined,
        groupIds: dateParams.groupIds ?? undefined,
        callMicrosoftIds: dateParams.callMicrosoftIds ?? undefined,
        page: currentPage,
        limit,
        sort: { column: apiSortColumn, direction: sortDirection },
        filters: filterArray.length > 0 ? filterArray : undefined,
      },
      { signal: controller.signal }
    )
      .then((res) => {
        if (controller.signal.aborted) return;
        setPagedData({
          data: (res.data ?? []).map(mapOverviewRowToCQDRow),
          total: res.total ?? 0,
          page: res.page ?? currentPage,
          limit: res.limit ?? limit,
          totalPages: res.totalPages ?? 1,
        });
      })
      .catch((err) => {
        if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
          console.error("Call Quality Detail fetch failed:", err);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
  }, [
    dateParams,
    currentPage,
    limit,
    apiSortColumn,
    sortDirection,
    filterArray,
  ]);

  useEffect(() => {
    fetchOverview();
    return () => {
      overviewAbortRef.current?.abort();
    };
  }, [fetchOverview]);

  useEffect(() => {
    if (!dateParams?.startDate || !dateParams?.endDate) return;
    const controller = new AbortController();
    fetchCallDetailOverviewColumnFilter({
      startDate: dateParams.startDate,
      endDate: dateParams.endDate,
      previousStartDate: dateParams.previousStartDate ?? "",
      previousEndDate: dateParams.previousEndDate ?? "",
      queueMicrosoftIds: dateParams.queueMicrosoftIds ?? null,
      agentMicrosoftIds: dateParams.agentMicrosoftIds ?? null,
      groupIds: dateParams.groupIds ?? null,
      callMicrosoftIds: dateParams.callMicrosoftIds ?? undefined,
      callDirectionTypes: [],
      page: 1,
      limit: 20,
      filterColumns: CQD_FILTER_COLUMN_NAMES.map((columnName) => ({
        columnName,
        searchValue: null,
      })),
    }, { signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted) setColumnFilterData(data ?? []);
      })
      .catch((err) => {
        if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
          console.error("CQD column filter fetch failed:", err);
        }
      });
    return () => controller.abort();
  }, [dateParams?.startDate, dateParams?.endDate, dateParams?.queueMicrosoftIds, dateParams?.agentMicrosoftIds, dateParams?.groupIds, dateParams?.callMicrosoftIds]);

  const columnUniqueValues = useMemo(() => {
    const acc: Record<string, string[]> = {};
    CQD_COLUMNS.forEach((col) => {
      if (!col.filterable || col.key === "detail") return;
      const apiName = CQD_TO_API_COLUMN[col.key] ?? col.key;
      const item = columnFilterData.find((f) => f.columnName === apiName);
      if (item?.columnValue?.length) {
        acc[col.key] = item.columnValue.map(String);
      } else {
        acc[col.key] = getUniqueValues(pagedData.data, col.key);
      }
    });
    return acc;
  }, [columnFilterData, pagedData.data]);

  const totalPages = Math.max(1, pagedData.totalPages);
  const totalItems = pagedData.total;
  const paginatedData = pagedData.data;
  const startIndex = totalItems > 0 ? (currentPage - 1) * limit + 1 : 0;
  const endIndex = Math.min(currentPage * limit, totalItems);

  const handleSort = useCallback((key: string) => {
    setSortKey(key);
    setSortDirection((prev) => (sortKey === key ? (prev === "asc" ? "desc" : "asc") : "desc"));
    setCurrentPage(1);
  }, [sortKey]);

  const handleValueToggle = useCallback((colKey: string, value: string) => {
    setFilters((prev) => {
      const arr = prev[colKey] || [];
      const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
      return { ...prev, [colKey]: next };
    });
    setCurrentPage(1);
  }, []);

  const handleClearColumn = useCallback((colKey: string) => {
    setFilters((prev) => ({ ...prev, [colKey]: [] }));
    setCurrentPage(1);
  }, []);

  const handleSelectAllColumn = useCallback((colKey: string, values: string[]) => {
    setFilters((prev) => ({ ...prev, [colKey]: values }));
    setCurrentPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchValues({});
    setCurrentPage(1);
    onClearExternalFilters?.();
  }, [onClearExternalFilters]);

  const hasActiveFilters = Object.values(filters).some((arr) => Array.isArray(arr) && arr.length > 0);

  return (
    <Card className="w-full overflow-hidden min-h-[300px] dark:bg-[#0C3451] dark:text-white dark:border-[#0C3451] rounded-lg">
      <CardHeader className="flex flex-row items-center justify-between sticky top-0 z-30 border-b rounded-t-lg bg-white dark:bg-[#0C3451] border-gray-200 dark:border-[#3C5C73]">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-primary dark:text-white">{title}</span>
          <InfoTooltip
            content="Stream‑level quality details across the call timeline, from call attempt to termination."
            iconClassName="text-primary dark:text-white"
          />
        </div>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="text-xs transition-opacity duration-200"
            onClick={clearFilters}
          >
            Clear Filters
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <div className="w-full relative h-[400px] overflow-auto">
            <table className="w-full call-journey-table" style={{ borderCollapse: "collapse", borderSpacing: 0 }}>
              <thead className="sticky top-0 z-50 bg-gray-50 dark:bg-[#3C5C73]">
                <tr className="text-xs font-medium text-teamscx-primary border-b border-gray-300 dark:text-white dark:border-white">
                  {CQD_COLUMNS.map((col) => {
                    const uniqueValues = columnUniqueValues[col.key] || [];
                    const search = searchValues[col.key] || "";
                    const selected = filters[col.key] || [];
                    const sortableKey = col.key === "QualityLevel" ? "QualityLevel" : col.key;
                    return (
                      <th
                        key={col.key}
                        className={cn(
                          "h-[45px] px-4 text-center font-medium transition-colors border-gray-300 dark:border-white align-middle border-b dark:border-t border-l-0 border-r-0"
                        )}
                        style={{ minWidth: col.minWidth, maxWidth: col.maxWidth }}
                      >
                        <div className="flex items-center gap-1">
                          <div
                            className="flex items-center gap-1 cursor-pointer font-medium"
                            onClick={() => handleSort(sortableKey)}
                          >
                            {col.title}
                            {sortKey === sortableKey &&
                              (sortDirection === "asc" ? (
                                <ChevronUp className="h-3 w-3" />
                              ) : (
                                <ChevronDown className="h-3 w-3" />
                              ))}
                          </div>
                          {col.filterable && (
                            <FilterDropdown
                              colKey={col.key}
                              colTitle={col.title}
                              uniqueValues={uniqueValues}
                              selected={selected}
                              searchValue={search}
                              onSearchChange={(value) => setSearchValues((p) => ({ ...p, [col.key]: value }))}
                              onValueToggle={(value) => handleValueToggle(col.key, value)}
                              onClearColumn={() => handleClearColumn(col.key)}
                              onSelectAllColumn={(values) => handleSelectAllColumn(col.key, values)}
                            />
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody className="border-t border-gray-300 dark:border-white">
                {loading ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <tr
                      key={idx}
                      className={cn(
                        "border-b border-gray-300 dark:border-white",
                        idx % 2 === 0 ? "bg-white dark:bg-[#0C3451]" : "bg-gray-50 dark:bg-[#3C5C73]"
                      )}
                    >
                      {CQD_COLUMNS.map((col) => (
                        <td
                          key={col.key}
                          className={cn(
                            "h-[45px] px-4 py-3 align-middle"
                          )}
                          style={{ minWidth: col.minWidth, maxWidth: col.maxWidth }}
                        >
                          <div className="h-4 w-full bg-gray-200 dark:bg-[#2C4C63] animate-pulse rounded" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : paginatedData.length === 0 ? (
                  <tr className="border-0">
                    <td
                      colSpan={CQD_COLUMNS.length}
                      className="text-center align-middle py-12 text-muted-foreground dark:text-white border-0"
                    >
                      <p className="text-sm">No call records were found</p>
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row, idx) => (
                    <React.Fragment key={row.callId}>
                      <tr
                        className={cn(
                          "transition-colors border-0",
                          idx % 2 === 0 ? "bg-white dark:bg-[#0C3451]" : "bg-gray-50 dark:bg-[#3C5C73]"
                        )}
                      >
                        <td
                          className={cn(
                            "h-[45px] px-4 py-3 text-xs text-teamscx-primary dark:text-white align-middle border-b border-gray-300 dark:border-white"
                          )}
                          style={{ minWidth: CQD_COLUMNS[0].minWidth }}
                        >
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-white/10 focus:outline-none"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedCallId((prev) => (prev === row.callId ? null : row.callId));
                              }}
                              aria-label={expandedCallId === row.callId ? "Collapse call journey" : "Expand call journey"}
                            >
                              {expandedCallId === row.callId ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                            <p className="truncate">{convertLocalTimezone(row.startTime)}</p>
                          </div>
                        </td>
                        <td
                          className={cn(
                            "h-[45px] px-4 py-3 text-xs text-teamscx-primary dark:text-white align-middle overflow-hidden border-b border-gray-300 dark:border-white"
                          )}
                          style={{ minWidth: CQD_COLUMNS[1].minWidth, maxWidth: CQD_COLUMNS[1].maxWidth }}
                        >
                          <TooltipProvider delayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <p className="truncate cursor-default block w-full">
                                  {row.participants || "-"}
                                </p>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-sm">
                                <p className="break-words whitespace-pre-wrap">{row.participants || "-"}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </td>
                        <td
                          className={cn(
                            "h-[45px] px-4 py-3 text-xs text-teamscx-primary dark:text-white align-middle border-b border-gray-300 dark:border-white"
                          )}
                          style={{ minWidth: CQD_COLUMNS[2].minWidth }}
                        >
                          <p className="truncate">{row.duration}</p>
                        </td>
                        <td
                          className={cn(
                            "h-[45px] px-4 py-3 text-xs align-middle border-b border-gray-300 dark:border-white"
                          )}
                          style={{ minWidth: CQD_COLUMNS[3].minWidth }}
                        >
                          <div className="inline-flex items-center gap-2">
                            <span className="text-primary dark:text-white whitespace-nowrap">
                              {row.qualityStatus === "Unclassified"
                                ? "N/A"
                                : row.qualityScore?.toFixed(1)}
                            </span>
                            <span
                              className="inline-flex px-2 py-0.5 rounded text-[10px] font-medium whitespace-nowrap"
                              style={getQualityBadgeStyle(row.qualityStatus, theme ?? "light")}
                            >
                              {row.qualityStatus}
                            </span>
                          </div>
                        </td>
                        <td
                          className={cn(
                            "h-[45px] px-4 py-3 text-xs align-middle border-b border-gray-300 dark:border-white"
                          )}
                          style={{ minWidth: CQD_COLUMNS[4].minWidth }}
                        >
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-[#23407a] dark:text-[#1681f6] font-medium hover:underline focus:outline-none"
                            onClick={() => onViewDetails?.(row)}
                          >
                            Detail
                          </button>
                        </td>
                      </tr>
                      {expandedCallId === row.callId && (
                        <tr className="border-0">
                          <td
                            colSpan={CQD_COLUMNS.length}
                            className={cn(
                              "text-center align-middle bg-white dark:bg-[#3C5C73] dark:text-white p-0 border-b border-gray-300 dark:border-white",
                              idx % 2 !== 0 && "bg-gray-50 dark:bg-[#3C5C73]"
                            )}
                          >
                            <div className="pt-4 pb-4 flex flex-col gap-0">
                              <div className="w-full px-2">
                                <h4 className="text-left text-sm font-semibold mb-0 ml-2 text-primary dark:text-white">Stream Timeline</h4>
                                <CallQualityStreamTimeline callUniqueId={row.callUniqueId} callId={row.callId} />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {!loading && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-300 dark:border-white bg-gray-50 dark:bg-[#3C5C73]">
            <div className="text-xs text-muted-foreground dark:text-gray-300">
              Showing {totalItems > 0 ? startIndex : 0}-{endIndex} of {totalItems}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="w-8 h-8 grid place-items-center rounded border border-gray-300 dark:border-[#4A6B85] disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-[#2C4C63] transition-colors"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                aria-label="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs px-1 text-primary dark:text-white">
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                className="w-8 h-8 grid place-items-center rounded border border-gray-300 dark:border-[#4A6B85] disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-[#2C4C63] transition-colors"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                aria-label="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CallQualityDetailWidget;
